import { usePeople, useAddPerson, useUpdatePerson, useDeletePerson, usePrompt } from "../hooks/hooks_index";
import { useState, useEffect } from "react";
import { ScrollToTop, Loading, MediaImage } from "../components/components_index";
import '../styles/EditPerson.css';

const token = "pk.eyJ1IjoianNwcmFkbGV5MDMiLCJhIjoiY21od2tod2I0MDBqdjJsb3U5dnhyZzBzeiJ9.mvEEnbzWVmP7S6FUVoyhqA";

export default function EditPerson() {
  const {data: people, isLoading, error} = usePeople();
  const addPerson = useAddPerson();
  const updatePerson = useUpdatePerson();
  const deletePerson = useDeletePerson();

  const [selectedID, setSelectedID] = useState("");
  const [IDlocked, setIDlocked] = useState(false);
  const [personData, setPersonData] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [scrollTrigger, setScrollTrigger] = useState(1);
  const [addressSuggestions, setAddressSuggestions] = useState([]);

  const arrayKeys = ["nicknames", "siblings", "children"];
  const objectKeys = ["b_details", "parents", "d_details", "address"];

  const keyVisuals = {
    "f_name": "First Name",
    "l_name": "Last Name",
    "AKA": "AKA",
    "nicknames": 'Nicknames',
    "b_details": {
      "f_name": "Birth First Name",
      "m_name" : "Birth Middle Name",
      "l_name": "Birth Last Name",
      "b_year": "Birth Year",
      "b_month": "Birth Month",
      "b_day": "Birth Day",
      "b_city": "Birth City",
      "b_state": "Birth State"
    },
    "parents": {
      "f_id": "Father ID",
      "m_id": "Mother ID"
    },
    "d_details": {
      "d_year": "Death Year",
      "d_month": "Death Month",
      "d_day": "Death Day",
      "d_city": "Death City",
      "d_state": "Death State"
    },
    "siblings": "Sibling IDs",
    "m_details": {
      "spouse": "Spouse ID", 
      "m_year": "Married Year",
      "m_month": "Married Month",
      "m_day": "Married Day",
      "div_year": "Divorced Year",
      "div_month": "Divorced Month",
      "div_day": "Divorced Day"
    },
    "children": "Children IDs",
    "email": "Email",
    "phone": "Phone #",
    "profile_pic": "Profile Pic Link",
    "address": {
      "address1": "Address",
      "address2": "Unit/Apt",
      "city": "City",
      "state": "State",
      "zip": "Zip Code",
      "formattedAddress": "Complete Address"
    }
  }

  function handleChange(e) {
    const value = e.target.value;
    setSelectedID(value)
  }

  function handleKeyPress(e) {
    if (e.key === "Escape") {
      setSelectedID("");
      e.target.blur();
    }
    if (e.key === "Enter") {
      handleSelectPerson();
    }
  }

  function handleSelectPerson() {
    if (selectedID.length === 7 && selectedID[0] === 'P') {
      setIDlocked(!IDlocked);

      if (people[selectedID]) {
        setPersonData(people[selectedID]);
      } else {
        setPersonData({})
      }
    }
  }

  function handleChangePerson() {
    setIDlocked(!IDlocked);
    setSubmitted(false);
  }

  function updateField(key, value) {
    setPersonData(prev => ({
      ...prev,
      [key]: value
    }));
  }

  const getArray = (key) => {
    const arr = personData[key];
    return Array.isArray(arr) ? arr : [""];
  };

  const updateArrayItem = (key, index, value) => {
    const arr = getArray(key);
    const newArr = [...arr];
    newArr[index] = value;
    updateField(key, newArr);
  };

  const addArrayItem = (key) => {
    const arr = getArray(key);
    updateField(key, [...arr, ""]);
  };

  const removeArrayItem = (key, index) => {
    if (index > 0) {
      const arr = getArray(key);
      const newArr = arr.filter((_, i) => i !== index);
      updateField(key, newArr);
    }
  };

  const getObject = (key, subKeys) => {
    const obj = personData[key] || {};
    const normalized = {};
    subKeys.forEach(subKey => {
      normalized[subKey] = obj[subKey] ?? "";
    });
    return normalized;
  };

  const updateObjectItem = (key, updates) => {
    const obj = personData[key] || {};
    updateField(key, {
      ...obj,
      ...updates
    });
  };

  const getObjectArray = (key, subKeys) => {
    const arr = personData[key];
    if (!Array.isArray(arr) || arr.length === 0) {
      return [Object.fromEntries(subKeys.map(k => [k, ""]))];
    }
    return arr.map(obj =>
      Object.fromEntries(subKeys.map(k => [k, obj?.[k] ?? ""]))
    );
  };

  const updateArrayObjectItem = (key, index, subKey, value) => {
    const arr = getObjectArray(key, mDetailsKeys);
    const newArr = [...arr];
    newArr[index] = { ...newArr[index], [subKey]: value };
    updateField(key, newArr);
  };

  const addArrayObjectItem = (key, subKeys) => {
    const arr = getObjectArray(key, subKeys);
    const blankObj = Object.fromEntries(subKeys.map(k => [k, ""]));
    updateField(key, [...arr, blankObj]);
  };

  const removeArrayObjectItem = (key, index) => {
    const arr = getObjectArray(key, mDetailsKeys);
    if (arr.length > 1) {
      const newArr = arr.filter((_, i) => i !== index);
      updateField(key, newArr);
    }
  };

  function cleanPersonData(data) {
    const cleaned = {};

    for (const [key, value] of Object.entries(data)) {
      if (value === "" || value === null || value === undefined) continue;
      if (Array.isArray(value)) {
        if (value.length === 0) continue;

        const cleanedArray = value
          .map(item => (typeof item === 'object' ? cleanPersonData(item) : item))
          .filter(item =>
            typeof item === 'object'
              ? Object.keys(item).length > 0
              : item !== ""
          );
        if (cleanedArray.length === 0) continue;
        cleaned[key] = cleanedArray;
        continue
      }
      if (typeof value === "object") {
        const cleanedObj = cleanPersonData(value);
        if (Object.keys(cleanedObj).length === 0) continue;
        cleaned[key] = cleanedObj;
        continue;
      }
      cleaned[key] = value;
    }
    if (data.address) {
      cleaned["address"]["formattedAddress"] = [
        data.address.address1,
        data.address.address2,
        `${data.address.city}, ${data.address.state} ${data.address.zip}`
      ]
      .join(", ");
    }

    return cleaned;
  }

  function handleSubmit() {
    const exists = Boolean(people[selectedID]);
    const cleaned = cleanPersonData(personData);

    const isEmpty = Object.keys(cleaned).length === 0;

    if (exists && isEmpty) {
      deletePerson.mutate(selectedID);
    } else if (!exists && isEmpty) {
      return;
    } else if (exists) {
      updatePerson.mutate({id: selectedID, data: cleaned});
    } else {
      addPerson.mutate({id: selectedID, data: cleaned});
    }

    setSubmitted(true);
    setScrollTrigger((scrollTrigger === 1) ? 2 : 1);
  }

  function handleClear() {
    setPersonData({});
  }

  async function searchAddress(query) {
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    // setSearchingAddress(true);
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?autocomplete=true&types=address&country=us&access_token=${token}`
    );
    const data = await response.json();
    // console.log(data.features)
    setAddressSuggestions(data.features ?? []);
    // setSearchingAddress(false);
  }

  async function selectSuggestion(feature) {
    const context = feature.context ?? [];

    const getContext = (type) =>
      context.find((item) => item.id.startsWith(type))?.text ?? "";

    const getShortCode = (type) =>
      context.find((item) => item.id.startsWith(type))?.short_code ?? "";

    // console.log(feature);
    // console.log(getContext("place"));

    const address1 = feature.place_name.split(",")[0];
    const city = getContext("place");
    const state = getShortCode("region").replace("US-", "");
    const zip = getContext("postcode");

    updateObjectItem("address", {
      address1,
      city,
      state,
      zip
    });
    setAddressSuggestions([]);
  }

  const renderField = (key) => {
    if (arrayKeys.includes(key)) {
      const arr = getArray(key);
      return(
        <div className="edit-input array-input">
          <label>{keyVisuals[key]}</label>
          {arr.map((val,i) => (
            <input
              key={`${key}-${i}`}
              type="text"
              value={val}
              onChange={(e) => updateArrayItem(key, i, e.target.value)}
              autoComplete='batman'
            />
          ))}
          <button
            type="button"
            className="add-array-item"
            onClick={() => addArrayItem(key)}
          >
            Another
          </button>
          <button
            type="button"
            className="remove-array-item"
            onClick={() => removeArrayItem(key, arr.length-1)}
          >
            Delete last
          </button>
        </div>
      );
    } else if (objectKeys.includes(key)) {
      let subKeys = [];
      let keyName = "";
      if (key === 'b_details') {
        subKeys = bDetailsKeys;
        keyName = "Birth Details";
      } else if (key === "d_details") {
        subKeys = dDetailsKeys;
        keyName = "Death Details";
      } else if (key === "parents") {
        subKeys = parentsKeys;
        keyName = "Parents";
      } else if (key === "address") {
        subKeys = addressKeys;
        keyName = "Address";
      }
      const obj = getObject(key, subKeys);
      return(
        <div className={`edit-input object-input${(key==='d_details'? " d-details" : "")}${(key==='address'? " address" : "")}`} key={key}>
          <label>{keyName}</label>
          {subKeys.map((subKey) => (
              <div className="edit-input object-element" key={subKey}>
                <div className="sub-label">{keyVisuals[key][subKey]}</div>
                <input
                  type="text"
                  value={obj[subKey]}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateObjectItem(key, {[subKey]: value});
                    if (subKey === "address1") {
                      searchAddress(value);
                    }
                  }}
                  className={`sub-input${"address" === key ? ` ${subKey}` : ""}`}
                  autoComplete='batman'
                  disabled={"formattedAddress" === subKey ? true : false}
                />
                {subKey === "address1" && addressSuggestions.length > 0 && (
                  <div className="address-popup">
                    {addressSuggestions.map((suggestion) => (
                      <div
                        key={suggestion.id}
                        className="address-suggestion"
                        onClick={() => selectSuggestion(suggestion)}
                      >
                        {suggestion.place_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
          ))}
        </div>
      )
    } else if (key === "m_details") {
      return (
        <div className="edit-input array-objects m_details" key="m_details">
          <label className="m-details">Marriage Details</label>
            <div className="object-box">
              {getObjectArray("m_details", mDetailsKeys).map((obj, i) => (
                <div className="edit-input object-input" key={i}>
                  {mDetailsKeys.map(subKey => (
                    <div className="edit-input object-element" key={subKey}>
                      <div className="sub-label">
                        {(() => {
                          const n = i + 1;
                          switch (n) {
                            case 1: return "1st";
                            case 2: return "2nd";
                            case 3: return "3rd";
                            default: return `${n}th`;
                          }
                        })()} {keyVisuals[key][subKey]}
                      </div>
                      <input
                        type="text"
                        value={obj[subKey]}
                        onChange={(e) =>
                          updateArrayObjectItem("m_details", i, subKey, e.target.value)
                        }
                        autoComplete='batman'
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => removeArrayObjectItem("m_details", i)}
                    className="delete-spouse"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          <button
            type="button"
            onClick={() => addArrayObjectItem("m_details", mDetailsKeys)}
          >
            Add another spouse
          </button>
        </div>
      )
    } else {
      return(
        <div className="edit-input">
          <label className={`single-input${(key==='f_name' || key==='email')?' no-space':""}`}>{keyVisuals[key]}</label>
          <input
            type="text"
            value={personData[key] ?? ""}
            onChange={(e) => updateField(key, e.target.value)}
            autoComplete='batman'
            className={`${key}-box`}
          />
        </div>
      )
    }
  }

  usePrompt(
    "Are you sure you want to leave this page? Any changed data will not be saved.",
    IDlocked && !submitted
  );

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!IDlocked || submitted) return;
      
      event.preventDefault();
      event.returnValue =
        "Are you sure you want to leave this page? Any changed data will not be saved.";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, [IDlocked, submitted]);

  if (isLoading) return <Loading/>;
  if (error) return <div>Error loading people</div>;

  const peopleList = Object.entries(people);

  const firstPerson = people[Object.keys(people)[0]];
  // const firstPerson = people["P420000"];
  const allKeys = Object.keys(firstPerson);
  const bDetailsKeys = Object.keys(firstPerson.b_details);
  const dDetailsKeys = Object.keys(firstPerson.d_details);
  const parentsKeys = Object.keys(firstPerson.parents);
  const addressKeys = Object.keys(firstPerson.address);
  const mDetailsKeys = Object.keys(firstPerson.m_details[0]);

  // console.log(allKeys)
  // console.log(Object.keys(keyVisuals))

  return (
    <div className='edit-person-page'>
      <div className='person-select'>
        <label className="edit-ID">
          <span className={`edit-label${IDlocked ? " disabled" : ""}`}>Select a Person:</span>
          <select
            value={selectedID}
            onChange={(e) => setSelectedID(e.target.value)}
            disabled={IDlocked}
            id="edit-person-select"
          >
            <option value="">New Person ID</option>
            {peopleList.map(([id, p]) => (
              <option key={id} value={id}>
                {p.AKA || `${p.f_name} ${p.l_name}`}
              </option>
            ))}
          </select>
        </label>
        <input
          id='new-person'
          type='text'
          placeholder='Add New ID'
          value={selectedID}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
          disabled={IDlocked}
          autoComplete='off'
        />
        <button 
          onClick={handleSelectPerson}
          disabled={IDlocked}
          id="select-button"
        >
          Select
        </button>
        <button 
          onClick={handleChangePerson}
          disabled={!IDlocked}
          id="change-button"
        >
          Change Person
        </button>
      </div>
      {(IDlocked) &&  (!submitted) &&
        <div className="edit-fields">
          <div className="disclaimer">If the person ID doesn't exist for a field, put the person's <b>name</b> instead</div>
          {allKeys.map(key => renderField(key))}
          <div className="bottom-buttons">
            <button
              type="button"
              className="submit-button"
              onClick={() => handleSubmit()}
            >
              Submit
            </button>
            <button
              type="button"
              className="clear-button"
              onClick={() => handleClear()}
            >
              Clear Fields
            </button>
          </div> 
        </div>
      }
      {(submitted) &&
        <div className="submitted-message">
          <div className="celebrate-wrapper">
            <div className="background-wrapper">
              <MediaImage mediaID={"Success_GIF.webp"} alt="" className="background-gif"/>
              <div className="submitted-text">
                Submitted Successfully
              </div>
            </div>
            <MediaImage mediaID={"Celebration.gif"} alt="Celebration GIF" className="celebration-gif"/>
          </div>
        </div>
      }
      {scrollTrigger && <ScrollToTop trigger={scrollTrigger} />}
    </div>
  );
}